import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { GoogleAnalyticsService } from './google-analytics.service';
import { SessionManagementService } from './session-management.service';
import { AuthenticationHttpService } from 'src/providers/authentication-http.service';

/**
 * Router Events Service
 *
 * Handles Angular Router events for cross-cutting concerns:
 * - Google Analytics tracking on successful navigation
 * - Session timeout reset on navigation
 *
 * Replaces UI-Router's onTransitionSuccess hook
 */
@Injectable({
    providedIn: 'root'
})
export class RouterEventsService {
    constructor(
        private router: Router,
        private googleAnalyticsService: GoogleAnalyticsService,
        private sessionManagementService: SessionManagementService,
        private authenticationHttpService: AuthenticationHttpService
    ) {}

    /**
     * Initialize router event listeners
     * Should be called once during app initialization
     */
    initialize(): void {
        // Listen for successful navigation events
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            this.handleNavigationSuccess(event);
        });
    }

    private handleNavigationSuccess(event: NavigationEnd): void {
        // Send page view to Google Analytics
        // Convert URL to route name for GA (e.g., /dashboard/activity -> dashboard.activity)
        const routeName = this.convertUrlToRouteName(event.urlAfterRedirects);
        this.googleAnalyticsService.sendRoute(routeName);

        // Reset session timeout on navigation (if user is authenticated)
        if (this.authenticationHttpService.isAuthenticated()) {
            this.sessionManagementService.resetSession();
        }
    }

    /**
     * Convert Angular Router URL to UI-Router-style route name for GA tracking
     * Example: /dashboard/activity -> dashboard.activity
     */
    private convertUrlToRouteName(url: string): string {
        // Remove query params and fragments
        const path = url.split('?')[0].split('#')[0];

        // Remove leading slash and convert slashes to dots
        const routeName = path
            .replace(/^\//, '')
            .replace(/\//g, '.');

        return routeName || 'root';
    }
}
